import { FormEvent, Fragment } from 'react';
import { BKJButton, BKJTextInput } from '@awais4123/fillify-design-library';
import { AppLink } from 'components/AppLink/AppLink';
import { PageWrapper } from 'components/PageWrapper';
import { PublicPageContentWrapper } from 'components/PublicPageContentWrapper';
import { useFormBehaviors } from 'hooks';
import {
  BelowLoginForm,
  ForgetPasswordText,
  ForgetPasswordWrapper,
  FormBody,
  FormSection,
  LoginFlex,
  LoginFlexContainer,
  LoginPaperContainer,
  LogoWrapper,
  SignUpButton,
  UnderLinedText,
} from 'pages/LoginPage/LoginPage.styled';
import { useTranslation } from 'hooks/useTranslation';
import { useUserActions } from 'store/reducers/auth/authReducer';
import { css } from 'styled-components';
import { VALIDATION_email, VALIDATION_password } from 'validations';
import { authResponseHandler } from 'pages/LoginPage/LoginPage.constants';
import { WhiteLabelAccessor } from 'utils/whiteLabelAccessor';
import { LoginFormSchema } from 'pages/LoginPage/LoginPage';
import { WhiteLabelMainLogo } from 'components/WhiteLabelMainLogo';

const initialState = {
  username: '',
  password: '',
};

interface APIErrors {
  type: string;
  message: string;
}

function DesktopLoginPageWrapper() {
  const { userLogin } = useUserActions();
  const { t } = useTranslation();
  const { isPetalProvider } = WhiteLabelAccessor;

  const { onSubmit, apiErrors, isSubmitting, isDisabled, onChange, values, fieldErrors } =
    useFormBehaviors<Required<LoginFormSchema>, APIErrors>({
      isDirtyCheckEnabled: true,
      initialState,
      validations: {
        username: VALIDATION_email,
        password: VALIDATION_password,
      },
      onSubmit: async ({ username, password }) => {
        const trimmedUsername = username.trim();
        const response = await userLogin({ username: trimmedUsername, password });
        return authResponseHandler(response);
      },
    });

  const translatedError =
    apiErrors?.type === 'NotFound' ? t('errors:NOT_FOUND') : apiErrors?.message;

  return (
    <PageWrapper $minWidth="100%" $maxWidth="100%" $width="100%">
      <PublicPageContentWrapper>
        <LoginPaperContainer>
          <LoginFlexContainer justifyContent="center" alignItems="center" flexDirection="column">
            <LogoWrapper>
              <WhiteLabelMainLogo width="200px" />
            </LogoWrapper>
            <LoginFlex justifyContent="center" margin="auto">
              <FormSection onSubmit={onSubmit}>
                <FormBody>
                  <BKJTextInput
                    label={t('auth:EMAIL_ADDRESS')}
                    value={values.username}
                    placeholder={t('auth:ENTER_YOUR_EMAIL_ADDRESS')}
                    type="username"
                    name="username"
                    error={fieldErrors.username}
                    required
                    onChange={onChange}
                    autoComplete="off"
                    css={css`
                      margin: 28px 0;
                    `}
                  />
                  <BKJTextInput
                    label={t('auth:PASSWORD')}
                    placeholder={t('auth:ENTER_YOUR_PASSWORD')}
                    value={values.password}
                    error={fieldErrors.password}
                    type="password"
                    name="password"
                    required
                    onChange={onChange}
                    autoComplete="off"
                    css={css`
                      margin-bottom: 28px;
                    `}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        onSubmit(e as unknown as FormEvent<HTMLFormElement>);
                      }
                    }}
                  />
                  {apiErrors && <span>{translatedError}</span>}
                  <BKJButton
                    variant="PurpleSolid"
                    type="submit"
                    disabled={isDisabled}
                    isLoading={isSubmitting}
                    css={css`
                      margin-top: 28px;
                      min-height: 48px;
                    `}
                  >
                    {t('auth:LOGIN')}
                  </BKJButton>
                  <ForgetPasswordWrapper>
                    <ForgetPasswordText>{t('auth:FORGOT_PASSWORD')}</ForgetPasswordText>
                    <AppLink to="/forgot">
                      <BKJButton variant="PurpleNoDecorations" data-cy="forgot_password">
                        <UnderLinedText>{t('auth:RESET_PASSWORD')}</UnderLinedText>
                      </BKJButton>
                    </AppLink>
                  </ForgetPasswordWrapper>
                </FormBody>
                <BelowLoginForm>
                  {!isPetalProvider && (
                    <Fragment>
                      <AppLink
                        to="/register"
                        css={css`
                          margin-top: 28px;
                          width: 100%;
                        `}
                      >
                        <SignUpButton variant="PurpleOutlined">
                          {t('auth:NOT_REGISTER')} &nbsp;
                          <UnderLinedText>{t('auth:SIGN_UP')}</UnderLinedText>
                        </SignUpButton>
                      </AppLink>
                    </Fragment>
                  )}
                </BelowLoginForm>
              </FormSection>
            </LoginFlex>
          </LoginFlexContainer>
        </LoginPaperContainer>
      </PublicPageContentWrapper>
    </PageWrapper>
  );
}

export default DesktopLoginPageWrapper;
